import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, MapPin, Briefcase, CheckCircle2, AlertTriangle, Upload, Zap, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicApplyPage() {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { job, sharer }
  const [err, setErr] = useState("");

  const [form, setForm] = useState({ candidate_name: "", candidate_email: "", candidate_phone: "", note: "", company: "" });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { job_title, sharer_name }

  useEffect(() => {
    const load = async () => {
      try {
        const url = `${BASE}/api/public/jobs/${jobId}${ref ? `?ref=${ref}` : ""}`;
        const r = await axios.get(url);
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This job is no longer available.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [jobId, ref]);

  const submit = async (e) => {
    e.preventDefault();
    if (!ref) {
      toast.error("Missing referral link — ask the person who shared this with you for the original link.");
      return;
    }
    if (!cv) {
      toast.error("Please attach your CV");
      return;
    }
    if (cv.size > 10 * 1024 * 1024) {
      toast.error("CV is too large (max 10 MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("candidate_name", form.candidate_name);
      fd.append("candidate_email", form.candidate_email);
      fd.append("candidate_phone", form.candidate_phone);
      fd.append("note", form.note);
      fd.append("company", form.company); // honeypot
      fd.append("ref", ref);
      fd.append("cv", cv);
      const r = await axios.post(`${BASE}/api/public/jobs/${jobId}/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone({ job_title: r.data.job_title, sharer_name: r.data.sharer_name });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sorry, something went wrong. Try again?");
    } finally {
      setBusy(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-yellow flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // ── Error / closed / not-found ──────────────────────────────────
  if (err) {
    return (
      <div className="min-h-screen bg-brand-yellow p-4 flex items-center justify-center">
        <div className="brutal bg-white max-w-md w-full p-8 text-center" data-testid="apply-error">
          <div className="w-14 h-14 bg-red-200 border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal mb-4 mx-auto">
            <AlertTriangle className="w-7 h-7" strokeWidth={2.4} />
          </div>
          <h2 className="font-display font-black text-2xl">Role unavailable</h2>
          <p className="text-sm text-neutral-700 mt-2">{err}</p>
          <Link to="/login" className="btn-brutal btn-primary inline-flex mt-6">Go to ReferPro</Link>
        </div>
      </div>
    );
  }

  // ── Success ─────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen bg-brand-yellow p-4 flex items-center justify-center">
        <div className="brutal bg-white max-w-md w-full p-8 text-center" data-testid="apply-success">
          <div className="w-16 h-16 bg-brand-green border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal mb-4 mx-auto">
            <CheckCircle2 className="w-8 h-8" strokeWidth={2.4} />
          </div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-brand-yellow border-2 border-black rounded-md mb-3">
            <PartyPopper className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span className="text-[10px] font-bold uppercase tracking-widest">Application received</span>
          </div>
          <h2 className="font-display font-black text-3xl">Thanks, you're in! 🎉</h2>
          <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
            Your application for <strong>{done.job_title}</strong> has been sent{done.sharer_name ? <> via <strong>{done.sharer_name}</strong></> : null}. The hiring team will be in touch within a few days.
          </p>
          <p className="text-xs text-neutral-500 mt-4">Tip: check your spam folder so you don't miss our reply.</p>
        </div>
      </div>
    );
  }

  const { job, sharer } = data || {};

  // ── Apply form ──────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-brand-yellow">
      {/* Top brand bar */}
      <header className="border-b-2 border-black bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
              <Zap className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <div className="font-display font-black leading-none text-lg">ReferPro</div>
          </div>
          <Link to="/login" className="text-[11px] font-bold underline text-neutral-700">For employees</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 grid lg:grid-cols-5 gap-5">
        {/* Job details */}
        <section className="lg:col-span-3 brutal bg-white p-5 sm:p-6 space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 border-2 border-black rounded-xl overflow-hidden flex items-center justify-center bg-brand-blue text-white shadow-brutal">
              {job.logo_url ? (
                <img src={job.logo_url} alt={job.title} className="w-full h-full object-cover" />
              ) : (
                <Briefcase className="w-5 h-5" strokeWidth={2.5} />
              )}
            </div>
            <div>
              <h1 className="font-display font-black text-3xl leading-tight" data-testid="apply-job-title">{job.title}</h1>
              {job.location && (
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-700 mt-1">
                  <MapPin className="w-4 h-4" /> {job.location}
                </span>
              )}
            </div>
          </div>

          {sharer && (
            <div className="brutal p-3 bg-brand-yellow flex items-center gap-3" data-testid="apply-shared-by">
              <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-black bg-white flex items-center justify-center font-display font-black flex-shrink-0">
                {sharer.avatar_url ? <img src={sharer.avatar_url} alt={sharer.name} className="w-full h-full object-cover" /> : sharer.name?.[0] || "?"}
              </div>
              <div className="text-xs leading-snug">
                <div className="font-bold">Shared with you by {sharer.name}</div>
                {sharer.office && <div className="text-neutral-700">{sharer.office}</div>}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1">About the role</h3>
            <p className="text-sm text-neutral-800 whitespace-pre-line leading-relaxed">{job.description || "No description provided."}</p>
          </div>
          {job.requirements && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1">Requirements</h3>
              <p className="text-sm text-neutral-800 whitespace-pre-line leading-relaxed">{job.requirements}</p>
            </div>
          )}
        </section>

        {/* Apply form */}
        <aside className="lg:col-span-2">
          <div className="brutal bg-white p-5 sm:p-6 lg:sticky lg:top-20">
            <h2 className="font-display font-black text-2xl leading-tight">Apply now</h2>
            <p className="text-xs text-neutral-600 mt-1">Takes 60 seconds. We'll review your CV and be in touch.</p>

            <form onSubmit={submit} className="mt-4 space-y-3" data-testid="apply-form">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest mb-1">Full name</label>
                <input className="input-brutal" required value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} data-testid="apply-name" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest mb-1">Email</label>
                <input type="email" className="input-brutal" required value={form.candidate_email} onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} data-testid="apply-email" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest mb-1">Phone</label>
                <input type="tel" className="input-brutal" required value={form.candidate_phone} onChange={(e) => setForm({ ...form, candidate_phone: e.target.value })} data-testid="apply-phone" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest mb-1">CV (PDF/DOC, max 10MB)</label>
                <label className="input-brutal flex items-center gap-2 cursor-pointer hover:bg-neutral-50">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm truncate">{cv ? cv.name : "Choose file…"}</span>
                  <input type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" required className="hidden" onChange={(e) => setCv(e.target.files?.[0] || null)} data-testid="apply-cv" />
                </label>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest mb-1">Anything else? <span className="text-neutral-500 font-normal normal-case tracking-normal">(optional)</span></label>
                <textarea rows={3} className="input-brutal" maxLength={500} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="apply-note" placeholder="A quick line about why you're a fit…" />
              </div>

              {/* Honeypot — hidden from real humans */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                aria-hidden="true"
              />

              <button type="submit" disabled={busy} className="btn-brutal btn-primary w-full py-3 mt-2" data-testid="apply-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />}
                {busy ? "Submitting…" : "Submit application"}
              </button>

              <p className="text-[10px] text-neutral-500 leading-relaxed mt-2 text-center">
                By applying you agree to be contacted by the hiring team. Your CV is stored securely and only shared internally.
              </p>
            </form>
          </div>
        </aside>
      </main>

      <footer className="text-center text-[11px] text-neutral-500 py-6">
        Powered by <Link to="/login" className="font-bold underline">ReferPro</Link>
      </footer>
    </div>
  );
}
